import { glp1BlogPost, spanishGlp1BlogPost } from "./glp1BlogPost";

export const spanishBlogContent = {
  [glp1BlogPost.slug]: spanishGlp1BlogPost,
  "gut-microbiome-why-your-gut-health-matters": {
    title: "El microbioma intestinal: por qué tu salud digestiva importa más de lo que crees",
    excerpt: "El microbioma intestinal influye en la inmunidad, el metabolismo, la digestión e incluso el estado de ánimo. Descubre por qué la diversidad es importante y qué hábitos sencillos favorecen la salud intestinal.",
    category: "Salud intestinal",
    readingTime: "5 min de lectura",
    callout: "Un microbioma intestinal diverso es uno de los mejores indicadores de un intestino sano, y los pequeños hábitos diarios pueden marcar una diferencia importante.",
    sections: [
      {
        heading: "Por qué la diversidad es clave para un intestino sano",
        body: [
          "Nuestro intestino grueso alberga aproximadamente 38 trillones de bacterias, una cifra tan enorme que puede resultar difícil de imaginar. Puede sonar alarmante porque solemos asociar las bacterias con infecciones y enfermedades. Sin embargo, estos microorganismos, conocidos en conjunto como flora o microbiota intestinal, desempeñan un papel fundamental en la salud e influyen en el sistema inmunitario, el metabolismo, el cerebro y la función digestiva.",
          "La investigación ha identificado más de 1.000 especies distintas de bacterias en el intestino humano, aunque cada persona suele albergar entre 100 y 150. Al igual que el ADN, esto hace que el microbioma intestinal de cada persona sea único.",
          "La diversidad bacteriana del intestino es un indicador de buena salud. Los estudios relacionan de forma constante una menor diversidad microbiana con distintas afecciones, como la enfermedad inflamatoria intestinal, la obesidad, la diabetes tipo 2 y enfermedades autoinmunes como la celiaquía, la diabetes tipo 1, el eccema y la psoriasis.",
          "Los estudios sobre la microbiota intestinal de personas con obesidad han identificado con frecuencia disbiosis, un desequilibrio en el que las bacterias perjudiciales superan a las beneficiosas. Por el contrario, una mayor diversidad bacteriana se considera un indicador sólido de salud intestinal y de mejores resultados generales de salud."
        ]
      },
      {
        heading: "¿Qué funciones cumple realmente un intestino sano?",
        body: [
          "Tener un intestino sano va mucho más allá de mantener deposiciones regulares o evitar molestias digestivas. Un microbioma intestinal que funciona bien puede favorecer la función inmunitaria, el metabolismo energético, la regulación hormonal y la salud cardiovascular.",
          "Gran parte de este beneficio depende de lo que hacen estas bacterias. Cuando fermentan compuestos alimentarios no digeribles, especialmente la fibra, producen como resultado ácidos grasos de cadena corta."
        ],
        list: [
          "El acetato es el ácido graso de cadena corta más abundante del intestino. Favorece el crecimiento de otras bacterias beneficiosas, participa en el metabolismo de las grasas y el colesterol y ayuda a regular el apetito.",
          "El propionato llega al hígado, donde participa en el metabolismo de la glucosa y ayuda a enviar señales de saciedad.",
          "El butirato es la principal fuente de energía de las células que recubren el colon. Desempeña un papel clave en el metabolismo de la glucosa y la energía, y ayuda a mantener el equilibrio de oxígeno del intestino, lo que contribuye a prevenir la disbiosis."
        ]
      },
      {
        heading: "El eje intestino-cerebro: tu intestino y tu mente están más conectados de lo que crees",
        body: [
          "Además de la digestión y el metabolismo, otro aspecto fascinante y bien establecido de la salud intestinal es el eje intestino-cerebro, la red de comunicación bidireccional entre los centros cognitivos y emocionales del cerebro y el intestino.",
          "Si alguna vez has sentido mariposas en el estómago al estar nervioso o preocupado, has experimentado el eje intestino-cerebro en acción.",
          "En la práctica, esto significa que el estado de ánimo y la salud mental pueden afectar directamente al funcionamiento intestinal, y viceversa. Un ejemplo claro es el síndrome del intestino irritable (SII). El SII se trata con frecuencia mediante terapias dirigidas al cerebro, como la terapia cognitivo-conductual, la hipnoterapia dirigida al intestino o algunos antidepresivos, que pueden reducir de forma significativa los síntomas digestivos. Del mismo modo, enfoques nutricionales como la dieta baja en FODMAP, dirigida directamente a los síntomas intestinales, también pueden mejorar el bienestar emocional y psicológico."
        ]
      },
      {
        heading: "Cuidar el intestino no tiene por qué ser complicado",
        body: [
          "El funcionamiento interno del intestino y su impacto en la salud pueden ser complejos. Sin embargo, cuidar la salud intestinal no tiene por qué serlo. Estos hábitos prácticos pueden marcar una diferencia real."
        ],
        subsections: [
          {
            heading: "Toma una amplia variedad de alimentos vegetales",
            body: [
              "Comer una gran variedad de alimentos vegetales es muy beneficioso para la salud intestinal. Te ayudará a alcanzar la recomendación diaria de 30 gramos de fibra, un objetivo que se estima que no alcanza el 90 % de la población europea. La fibra favorece deposiciones regulares y fáciles de expulsar, y alimenta a las bacterias beneficiosas del intestino."
            ],
            list: [
              "Intenta consumir 30 plantas distintas cada semana, entre fruta, verdura, legumbres, cereales, hierbas y semillas.",
              "Incluye fruta en el desayuno.",
              "Procura que la mitad del plato sean verduras en la comida y la cena.",
              "Elige cereales integrales para el desayuno, como avena o cereales de trigo integral.",
              "Cambia el pan, las galletas saladas, los wraps, la pasta y el arroz blancos por opciones integrales.",
              "Toma fruta o un puñado pequeño de frutos secos entre horas.",
              "Añade semillas de chía, lino, sésamo o calabaza a las comidas para aumentar fácilmente la fibra.",
              "Intenta realizar al menos dos días sin carne por semana.",
              "Incluye legumbres como garbanzos, lentejas o alubias al menos cada dos días."
            ]
          },
          {
            heading: "Mantén horarios regulares de comidas",
            body: [
              "Las comidas regulares son importantes para mantener sanas las bacterias intestinales. Un patrón irregular puede alterar la microbiota y favorecer síntomas como hinchazón y dolor abdominal. Intenta hacer tres comidas al día a horas parecidas, junto con uno o dos tentempiés equilibrados y variados."
            ]
          },
          {
            heading: "Mantén una buena hidratación",
            body: [
              "Beber suficientes líquidos, aproximadamente ocho vasos al día, es esencial para la salud intestinal. La hidratación ayuda a mantener el revestimiento del intestino y favorece deposiciones blandas y fáciles de expulsar. El té, el café y los batidos también cuentan como líquidos, no solo el agua."
            ]
          },
          {
            heading: "Gestiona el estrés",
            body: [
              "Gestionar el estrés es un aspecto de la salud intestinal al que a menudo no se presta suficiente atención. Un nivel elevado de estrés puede afectar directamente al intestino y desencadenar hinchazón, retortijones, cambios en el ritmo intestinal y dolor abdominal. La atención plena, los ejercicios de respiración, el yoga, la meditación o reservar tiempo para descansar pueden favorecer un sistema digestivo más sano."
            ]
          },
          {
            heading: "Mantente activo",
            body: [
              "La actividad física regular es otro pilar importante de la salud intestinal. El ejercicio estimula las contracciones naturales del intestino, favorece el tránsito digestivo y reduce la probabilidad de estreñimiento e hinchazón. Incluso caminar 30 minutos la mayoría de los días puede tener un efecto significativo."
            ]
          }
        ]
      },
      {
        heading: "Cuándo solicitar atención médica",
        body: [
          "Aunque estos hábitos pueden contribuir a la salud intestinal, es importante escuchar al cuerpo. Ponte en contacto cuanto antes con tu médico si presentas alguno de los siguientes síntomas:"
        ],
        list: [
          "Pérdida de peso inexplicada o involuntaria",
          "Sangre en las heces o sangrado rectal",
          "Diarrea o estreñimiento persistentes que no mejoran",
          "Dolor abdominal intenso o que empeora",
          "Dificultad para tragar",
          "Náuseas o vómitos persistentes",
          "Un cambio evidente del ritmo intestinal que dura más de unas semanas",
          "Cansancio o anemia sin explicación",
          "Despertarse por la noche para defecar"
        ],
        bodyAfter: [
          "Estos síntomas pueden indicar en ocasiones una afección subyacente que requiere valoración profesional y no deben ignorarse. Si notas que algo no va bien y persiste durante más de dos o tres semanas, confía en tu percepción y solicita asesoramiento.",
          "Los consejos de este artículo están pensados para apoyar la salud y el bienestar intestinal general. No sustituyen el asesoramiento médico o nutricional personalizado. Si deseas una orientación adaptada sobre tu alimentación y salud intestinal, un dietista-nutricionista puede ayudarte a crear un plan adecuado a tus necesidades y objetivos."
        ]
      }
    ],
    references: [
      {
        label: "Valdes, A.M., Walter, J., Segal, E. y Spector, T.D. (2018) «Papel de la microbiota intestinal en la nutrición y la salud», BMJ, 361, k2179."
      },
      {
        label: "Carabotti, M., Scirocco, A., Maselli, M.A. y Severi, C. (2015) «El eje intestino-cerebro: interacciones entre la microbiota entérica y los sistemas nerviosos central y entérico», Anales de Gastroenterología, 28(2), pp. 203-209."
      },
      {
        label: "Conlon, M.A. y Bird, A.R. (2015) «El impacto de la alimentación y el estilo de vida en la microbiota intestinal y la salud humana», Nutrients, 7(1), pp. 17-44."
      },
      {
        label: "Monda, V. et al. (2017) «El ejercicio modifica la microbiota intestinal con efectos positivos para la salud», Medicina Oxidativa y Longevidad Celular, 2017."
      },
      {
        label: "Hou, K. et al. (2022) «La microbiota en la salud y la enfermedad», Transducción de Señales y Terapia Dirigida, 7, p. 135."
      },
      {
        label: "Ahmed, F. et al. (2025) «Relación entre el estrés, la alimentación y la microbiota intestinal: un estudio transversal», PMC, PMID: 41102818."
      }
    ]
  },
  "understanding-chronic-constipation-causes-diet-solutions": {
    title: "Comprender el estreñimiento crónico: causas, alimentación y soluciones basadas en la evidencia",
    excerpt: "El estreñimiento crónico es frecuente, pero el apoyo adecuado depende de la causa. Descubre el papel de la fibra, la hidratación y distintas estrategias dietéticas basadas en la evidencia.",
    category: "Estreñimiento",
    readingTime: "5 min de lectura",
    callout: "El estreñimiento puede estar relacionado con la alimentación, pero también con medicamentos, hormonas, movilidad, enfermedades y trastornos intestinales; por ello, los síntomas persistentes requieren una valoración adecuada.",
    sections: [
      {
        heading: "¿Qué es el estreñimiento crónico?",
        body: [
          "El estreñimiento crónico se caracteriza por deposiciones poco frecuentes o heces difíciles de expulsar que pueden requerir esfuerzo y que persisten en el tiempo. La escala de Bristol muestra distintas consistencias de las heces; los tipos 1 y 2 son los que se asocian con mayor frecuencia al estreñimiento."
        ]
      },
      {
        heading: "¿A quién afecta con más frecuencia?",
        body: [
          "Se estima que el estreñimiento crónico afecta al 10-16 % de la población mundial. Es más frecuente en adultos mayores de 65 años y en mujeres, especialmente durante el embarazo y la menopausia debido a los cambios hormonales."
        ]
      },
      {
        heading: "¿Qué causa el estreñimiento?",
        body: [
          "El estreñimiento puede estar relacionado con la alimentación, aunque también existen muchos factores no dietéticos que pueden influir."
        ],
        subsections: [
          {
            heading: "Causas relacionadas con la alimentación",
            list: ["Dieta baja en fibra", "Consumo insuficiente de líquidos"]
          },
          {
            heading: "Causas no dietéticas",
            list: [
              "Movilidad reducida y estilo de vida sedentario",
              "Cambios hormonales",
              "Medicamentos",
              "Enfermedades metabólicas",
              "Enfermedades neurológicas",
              "Causas mecánicas",
              "Cáncer colorrectal",
              "Enfermedad diverticular",
              "Trastornos digestivos funcionales",
              "Contexto socioeconómico",
              "Salud mental",
              "Trastornos anorrectales"
            ]
          }
        ]
      },
      {
        heading: "El papel de la fibra en el tratamiento del estreñimiento",
        body: [
          "Se recomienda que los adultos consuman al menos 30 gramos de fibra al día, pero se estima que el 90 % no alcanza este objetivo. Si deseas aumentar la fibra, hazlo de forma gradual para evitar molestias y síntomas digestivos.",
          [
            "Aumentar la fibra puede aliviar el estreñimiento en personas con un tiempo de tránsito colónico normal. Sin embargo, quienes presentan tránsito lento pueden notar un empeoramiento, ya que la fibra aumenta el volumen de las heces y es fermentada por las bacterias intestinales, lo que libera gases y puede causar molestias. Si quieres saber más sobre las bacterias intestinales, ",
            {
              label: "lee el artículo sobre el microbioma intestinal",
              href: "/blog/gut-microbiome-why-your-gut-health-matters"
            },
            "."
          ],
          "La fibra puede clasificarse de forma general en soluble e insoluble, y cada tipo actúa de manera ligeramente distinta.",
          "La fibra soluble se disuelve en agua y forma una sustancia similar a un gel en el intestino, lo que ayuda a ablandar las heces. Se encuentra en la avena, las legumbres y las semillas de lino.",
          "La fibra insoluble no se disuelve en agua. Aumenta el volumen de las heces y ayuda a que avancen con mayor rapidez por el intestino. Se encuentra en el pan y los cereales integrales, el salvado, la piel de frutas y verduras y los frutos secos.",
          "La mayoría de los alimentos contiene una combinación de fibra soluble e insoluble, y ambas desempeñan un papel importante en la salud digestiva. En relación con la fibra, la variedad es fundamental.",
          "Aquí tienes una tabla que resume el contenido de fibra por ración de algunos alimentos ricos en fibra:"
        ],
        table: {
          headers: ["Alimento", "Ración", "Fibra en gramos"],
          rows: [
            ["Aguacate", "1/2 mediano (75 g)", "5,0"],
            ["Manzana con piel", "1 mediana (182 g)", "4,4"],
            ["Coles de Bruselas cocidas", "1 taza (156 g)", "4,1"],
            ["Copos de avena secos", "1/2 taza (40 g)", "4,0"],
            ["Boniato asado", "1 mediano (130 g)", "3,8"],
            ["Zanahoria cruda", "1 taza (128 g)", "3,6"],
            ["Almendras", "28 g", "3,5"],
            ["Palomitas sin aceite", "3 tazas (24 g)", "3,5"],
            ["Pipas de girasol", "1/4 de taza (32 g)", "3,1"],
            ["Mango", "1 taza (165 g)", "2,6"],
            ["Col rizada cocida", "1 taza (130 g)", "2,6"],
            ["Pan integral", "1 rebanada (28 g)", "1,9"],
            ["Semillas de calabaza", "28 g", "1,8"]
          ]
        }
      },
      {
        heading: "No olvides la hidratación",
        body: [
          "Mantener una buena hidratación es clave para tratar el estreñimiento, ya que los líquidos ayudan a conservar las heces blandas y el intestino lubricado. Como orientación general, la mayoría de los adultos necesita entre 30 y 35 mls por kg de peso corporal al día, lo que suele equivaler a unos 2 litros. El agua es la mejor fuente de hidratación, aunque las bebidas calientes, la leche, los zumos, los batidos y las frutas y verduras con alto contenido de agua también contribuyen.",
          "Para la mayoría de las personas, el agua es suficiente y las bebidas con electrolitos no son necesarias, salvo en climas muy calurosos o durante la recuperación tras ejercicio intenso y prolongado, como correr o montar en bicicleta."
        ]
      },
      {
        heading: "Intervenciones para el estreñimiento basadas en la evidencia",
        body: [
          "La Asociación Británica de Dietética ha publicado recientemente unas guías completas y basadas en la evidencia sobre el tratamiento dietético del estreñimiento crónico. Estos son sus principales hallazgos."
        ],
        subsections: [
          {
            heading: "Cáscara de psyllium",
            body: [
              "La cáscara de psyllium, también llamada ispágula, es una forma bien estudiada de fibra soluble que actúa como laxante suave al formar un gel en el intestino, absorber agua y aumentar el volumen de las heces. Puede ablandar las heces, reducir el esfuerzo y aumentar la frecuencia de las deposiciones. Es importante tomarla con abundante agua e introducirla de forma gradual."
            ]
          },
          {
            heading: "Probióticos",
            body: [
              "Los probióticos pueden aliviar algunos síntomas del estreñimiento, aunque todavía no se conocen las cepas más beneficiosas. Puede probarse un probiótico durante unas 12 semanas y observar los síntomas. Si no hay mejoría, puede suspenderse. En cuanto a los probióticos, es importante recordar que un precio más alto no significa que sea mejor."
            ]
          },
          {
            heading: "Kiwi",
            body: [
              "El kiwi puede ayudar a ablandar las heces y aumentar la frecuencia de las deposiciones, de manera similar al psyllium. Incluir dos kiwis verdes al día es una estrategia sencilla y práctica."
            ]
          },
          {
            heading: "Pan de centeno",
            body: [
              "El pan de centeno ha resultado más eficaz que el pan convencional para aumentar la frecuencia de las deposiciones. Sin embargo, los estudios utilizaron grandes cantidades, aproximadamente cuatro rebanadas al día de pan de centeno tradicional, algo que puede no ser accesible o realista para muchas personas."
            ]
          },
          {
            heading: "Óxido de magnesio",
            body: [
              "Los suplementos de óxido de magnesio pueden aliviar el estreñimiento al aumentar la frecuencia, ablandar las heces y reducir el esfuerzo, la hinchazón y el dolor abdominal. Consulta a un profesional sanitario antes de tomarlos si tienes una enfermedad renal."
            ]
          },
          {
            heading: "Agua con alto contenido mineral",
            body: [
              "El agua con alto contenido mineral ha mostrado beneficios clínicos frente al agua con bajo contenido mineral. Sin embargo, el agua con las concentraciones utilizadas en los estudios todavía no está disponible de forma generalizada en muchos países europeos y puede no ser adecuada para personas con enfermedad renal."
            ]
          }
        ]
      },
      {
        heading: "Cuándo acudir al médico: síntomas de alarma",
        body: [
          "Consulta cuanto antes con un profesional sanitario si presentas alguno de los siguientes síntomas:"
        ],
        list: [
          "Cambios del ritmo intestinal después de los 50 años",
          "Aparición repentina de estreñimiento en una persona mayor",
          "Sangre en las heces o en el inodoro",
          "Pérdida de peso involuntaria",
          "Niveles bajos de hierro",
          "Antecedentes personales o familiares de cáncer colorrectal",
          "Despertarse por la noche para defecar",
          "Incontinencia",
          "Heces pálidas",
          "Mucosidad en las heces",
          "Dolor abdominal intenso"
        ]
      }
    ],
    references: [
      {
        label: "Forootan, M., Bagheri, N. y Darvishi, M. (2018) «Estreñimiento crónico: revisión de la literatura», Medicine, 97(20), e10631."
      },
      {
        label: "Dimidi, E. et al. (2025) «Guías de la Asociación Británica de Dietética para el tratamiento dietético del estreñimiento crónico en adultos», Revista de Nutrición Humana y Dietética, 38(5), e70133."
      }
    ]
  },
  "pcos-new-name-pmos-polyendocrine-metabolic-ovarian-syndrome": {
    title: "SOP tiene un nuevo nombre: bienvenido el SOMP, el nuevo integrante del grupo",
    excerpt: "El SOP se ha renombrado oficialmente como SOMP para reflejar mejor los aspectos hormonales, metabólicos y ováricos de esta condición compleja.",
    category: "SOMP",
    readingTime: "8 min de lectura",
    callout: "El SOMP es mucho más que una afección de los ovarios. El nuevo nombre refleja los sistemas hormonales y metabólicos implicados, sin dejar fuera la disfunción ovárica.",
    sections: [
      {
        heading: "Por qué cambió el nombre",
        body: [
          "El 12 de mayo de este año se cambió oficialmente el nombre de la afección conocida como SOP, síndrome de ovario poliquístico, por el de SOMP: síndrome ovárico metabólico poliendocrino. Este cambio se hizo para reflejar la complejidad de una condición que afecta a un estimado de 170 millones de mujeres en edad reproductiva en todo el mundo.",
          "Analicemos el razonamiento detrás del nuevo nombre."
        ],
        subsections: [
          {
            heading: "Síndrome",
            body: [
              "La palabra síndrome no ha cambiado. Por definición, un síndrome es un conjunto variable de signos y síntomas que aparecen juntos en una condición específica, en este caso, el SOMP. Los mecanismos subyacentes exactos y la forma en que se manifiesta la afección pueden variar enormemente de una persona a otra."
            ]
          },
          {
            heading: "Ovárico",
            body: [
              "No todas las mujeres diagnosticadas con SOMP presentarán realmente quistes en los ovarios al examinarlas, y esa es precisamente la razón por la que el nombre anterior, SOP, generó confusión durante tantos años. Aun así, la disfunción ovárica y la presencia de quistes, que en realidad son folículos ováricos inmaduros, siguen formando parte de los criterios diagnósticos, por lo que se mantuvo una referencia a los ovarios en el nombre."
            ]
          },
          {
            heading: "Metabólico",
            body: [
              "Las anomalías metabólicas son fallos en la forma en que el cuerpo procesa elementos como el azúcar en sangre y el colesterol, y son inherentes al SOMP. Estas incluyen resistencia a la insulina, obesidad, diabetes tipo 2, colesterol alto, presión arterial alta y mayor riesgo de enfermedad cardíaca. Por eso se añadió la palabra metabólico al nombre."
            ]
          },
          {
            heading: "Poliendocrino",
            body: [
              "Poli significa muchos, y endocrino se refiere a las hormonas y sus vías de acción. Esto pone de relieve que el SOMP afecta a numerosos sistemas hormonales del cuerpo, provocando alteraciones que incluyen una mayor liberación de andrógenos por parte de los ovarios y las glándulas suprarrenales, resistencia a la insulina que conduce a una mayor liberación de insulina por parte del páncreas, y alteraciones en la vía hormonal entre el hipotálamo y los ovarios."
            ]
          }
        ]
      },
      {
        heading: "¿Cómo se diagnostica el SOMP?",
        body: [
          "El proceso diagnóstico no ha cambiado con el cambio de nombre. El SOMP se diagnostica utilizando los criterios de Rotterdam. Para recibir un diagnóstico, la persona debe cumplir al menos 2 de los siguientes 3 criterios, y debe ser adulta, 20 años o más. Pueden aplicarse criterios diferentes para adolescentes."
        ],
        list: [
          "Ciclos menstruales irregulares o ausencia de menstruación.",
          "Producción excesiva de hormonas masculinas, también conocida como hiperandrogenismo, detectada en el examen clínico o en análisis de sangre. Esto puede manifestarse como exceso de vello facial, en el pecho o en la espalda; acné facial y/o corporal; caída del cabello o pérdida de cabello de patrón masculino; o niveles elevados de testosterona en un análisis de sangre.",
          "Presencia de folículos ováricos inmaduros, comúnmente llamados quistes, en la ecografía vaginal."
        ]
      },
      {
        heading: "Alimentación y SOMP",
        body: [
          "Las Guías Internacionales de 2023 para el manejo del SOMP recomiendan cambios en el estilo de vida como tratamiento de primera línea para todas las mujeres con esta condición, lo cual incluye cambios en la alimentación.",
          "Aunque no se ha demostrado que ningún tipo de dieta en particular sea superior a otro, existen muchos cambios en la alimentación que las mujeres que viven con SOMP pueden hacer para respaldar su salud.",
          "Es importante señalar que la pérdida de peso se recomienda muy a menudo como tratamiento del SOMP. Pero la realidad es que perder peso puede resultar muy difícil para las mujeres con SOMP. Los mecanismos exactos no se comprenden del todo, pero se reconoce que muchas mujeres con SOMP presentan procesos subyacentes que favorecen un mayor aumento de peso y un IMC más elevado, lo que hace que perder peso de forma sostenible sea extremadamente complicado para muchas mujeres.",
          "Por eso es importante subrayar que los cambios en el estilo de vida favorecen la salud general y tienen un impacto positivo independientemente de que se logre o no perder peso. Los profesionales de la salud deberían comunicar esto con claridad a todas las mujeres que viven con SOMP, con el fin de reducir al mínimo el estigma relacionado con el peso. También vale la pena señalar que la prevalencia de trastornos alimentarios y de la conducta alimentaria es mayor entre las mujeres que viven con SOMP, por lo que los consejos sobre estilo de vida deben abordarse con amabilidad, respeto y siempre trabajando hacia una relación positiva con la comida.",
          "Aunque ningún tipo de dieta en particular ha demostrado ser superior a otro para el manejo del SOMP, hay muchos cambios que las mujeres pueden hacer para respaldar su salud y controlar los síntomas. Aquí van algunos ejemplos."
        ]
      },
      {
        heading: "Priorizar los carbohidratos de liberación lenta",
        body: [
          "La resistencia a la insulina afecta a aproximadamente el 75% de las personas que viven con SOMP, así que vale la pena entender en qué consiste realmente.",
          "Piensa en tus células como puertas, y en la insulina, la hormona que libera el páncreas en respuesta al aumento de la glucosa en sangre, como la llave para esa puerta. La glucosa en sangre sube después de comer, especialmente tras consumir carbohidratos. La glucosa es la moneda energética del cuerpo y la fuente de combustible preferida para nuestras células, tejidos y órganos. La insulina actúa como la llave que abre la puerta de la célula para dejar entrar la glucosa; pero con la resistencia a la insulina, la cerradura está algo oxidada. A la insulina le cuesta dejar entrar la glucosa en las células, así que el exceso de glucosa sigue circulando en el torrente sanguíneo, y el páncreas libera cada vez más insulina intentando bajar esos niveles.",
          "La resistencia a la insulina sostenida y la hiperinsulinemia resultante aumentan el riesgo de desarrollar diabetes tipo 2. En quienes tienen SOMP, esto también impulsa una sobreproducción de andrógenos, lo que provoca menstruaciones irregulares, acné, vello facial y otros síntomas relacionados con niveles elevados de testosterona."
        ]
      },
      {
        heading: "Incluir omega-3 en la dieta",
        body: [
          "El SOMP está asociado a una inflamación crónica de bajo grado en el cuerpo, lo que aumenta el riesgo de mayor resistencia a la insulina, diabetes tipo 2 y enfermedad cardíaca. El omega-3 es un tipo de ácido graso esencial muy conocido por sus propiedades antiinflamatorias. Aunque incluir omega-3 en la dieta no resolverá todos los problemas, es una estrategia dietética sólida para ayudar a reducir esa inflamación de bajo grado, al tiempo que respalda la salud hormonal, cerebral y cardíaca.",
          "El pescado azul es la mejor fuente de omega-3, ya que contiene EPA y DHA, dos formas de omega-3 que el cuerpo puede aprovechar eficientemente. Buenas fuentes incluyen el salmón, la trucha, la caballa, las sardinas, las anchoas y el atún. Comer pescado azul dos veces por semana aporta suficiente omega-3.",
          "Otras fuentes dietéticas de omega-3 son las semillas y los frutos secos, que contienen principalmente ALA, una forma de omega-3 que el cuerpo no aprovecha tan eficientemente como el EPA y el DHA. Aun así, vale la pena incluirlos en la dieta, ya que ofrecen muchos otros nutrientes y respaldan la salud general. Si no comes pescado, ya sea por preferencia o porque sigues una dieta vegetariana o vegana, podría valer la pena considerar un suplemento de omega-3."
        ]
      },
      {
        heading: "Llena tu plato de color",
        body: [
          "Intenta que haya la mayor cantidad de color posible en tu plato, en forma de frutas y verduras. Esto aporta fibra y polifenoles, que son compuestos con propiedades antiinflamatorias. Procurar que la mitad de tu plato en cada comida sea fruta y verdura ayudará a asegurar que obtengas suficiente fibra a lo largo del día, lo que además ralentiza aún más la liberación de glucosa en el torrente sanguíneo después de las comidas.",
          "Esto también favorece el control del peso, ya que incrementa los niveles de saciedad después de las comidas, y alimenta a tu flora intestinal, lo que a su vez respalda la regulación del apetito y el metabolismo mediante la producción de ácidos grasos de cadena corta. Estos, junto con los polifenoles, también ayudan a reducir la inflamación."
        ]
      },
      {
        heading: "Entrenamiento de fuerza",
        body: [
          "Aunque no está directamente relacionado con la alimentación, el entrenamiento de fuerza favorece la salud de quienes tienen SOMP al modificar la composición corporal y aumentar la masa muscular, que es metabólicamente más activa que el tejido graso, y al mejorar la resistencia a la insulina. Dos sesiones de entrenamiento de fuerza a la semana pueden ser suficientes para notar beneficios en la salud."
        ]
      },
      {
        heading: "Suplementación con inositol",
        body: [
          "Estudios científicos han demostrado que el inositol es tan eficaz como la metformina para reducir la resistencia a la insulina. También se ha demostrado que mejora el perfil lipídico y las tasas de diabetes gestacional, reduce los niveles de andrógenos, disminuye los antojos y ayuda a regular los ciclos menstruales. Si estás considerando empezar a tomar inositol, consulta siempre primero con un profesional médico o de nutrición sobre la dosis, los efectos secundarios y las contraindicaciones, por ejemplo comenzar a tomarlo al mismo tiempo que la metformina."
        ]
      },
      {
        heading: "En resumen",
        body: [
          "El SOMP es una condición compleja que involucra muchos factores, y cambios en el estilo de vida pueden tener un efecto positivo en mujeres con SOMP. Las recomendaciones anteriores no son, ni mucho menos, los únicos cambios que puedes hacer: hay más apoyo disponible.",
          "También vale la pena señalar que los consejos anteriores son genéricos. Para un apoyo nutricional adaptado a tu propio cuerpo, tus síntomas y tu estilo de vida, siempre es mejor trabajar con un dietista-nutricionista colegiado.",
          [
            "Si tienes SOMP y estás lista para sentir un mayor control sobre tu estilo de vida, tu alimentación y tus síntomas, me encantaría ayudarte. ",
            {
              label: "Reserva una cita aquí",
              href: "https://calendly.com/iballa-mtzyanes/consulta-inicial?month=2026-07"
            },
            " y construyamos juntas un plan que realmente funcione para tu cuerpo."
          ]
        ]
      }
    ],
    references: [
      {
        label: "Teede, H. et al. Guía internacional basada en la evidencia para la evaluación y el manejo del síndrome de ovario poliquístico (SOMP) 2023, Monash University, actualizada el 12 de junio de 2026."
      },
      {
        label: "Cree, M.G. et al. (2026) «Polyendocrine Metabolic Ovarian Syndrome, the New Name for Polycystic Ovary Syndrome: A Multistep Global Consensus Process», The Lancet, 407."
      },
      {
        label: "Szkodziak, P. et al. (2025) «Insulin Resistance in Polycystic Ovary Syndrome Phenotypes and the Vicious Cycle Model in Its Etiology», Scientific Reports, 15, 42649."
      },
      {
        label: "Armanini, D., Boscaro, M., Bordin, L. y Sabbadin, C. (2022) «Controversies in the Pathogenesis, Diagnosis and Treatment of PCOS: Focus on Insulin Resistance, Inflammation, and Hyperandrogenism», International Journal of Molecular Sciences, 23(8), 4110."
      },
      {
        label: "Duan, M. et al. (2026) «Effects of Inositol in Women with Polycystic Ovary Syndrome: An Umbrella Review of Meta-Analyses from Randomized Controlled Trials», Frontiers in Endocrinology, 17, 1741509."
      }
    ]
  }
};

function mergeLocalizedValue(base, localized) {
  if (localized === undefined) return base;
  if (Array.isArray(base) && Array.isArray(localized)) {
    return localized.map((item, index) => mergeLocalizedValue(base[index], item));
  }
  if (base && localized && typeof base === "object" && typeof localized === "object") {
    return Object.fromEntries(
      Array.from(new Set([...Object.keys(base), ...Object.keys(localized)]))
        .map((key) => [key, mergeLocalizedValue(base[key], localized[key])])
    );
  }
  return localized;
}

export function localizeBlogPosts(posts, language) {
  if (!language?.startsWith("es")) return posts;
  return posts.map((post) => mergeLocalizedValue(post, spanishBlogContent[post.slug]));
}
